import { parseMessageKey, type MessageParams } from '@netdeck/contracts';
import type { TFunction } from 'i18next';

/**
 * Сборка текста отказа на языке того, кто смотрит (§11, фаза 10, задача 3).
 *
 * С сервера приезжает машинный ключ и данные для подстановки —
 * `i18n:errors.cameras.disabled:{"name":"Склад-1"}`, — а текст собирается
 * здесь. До фазы 10 сервер присылал готовую фразу, и язык сообщения об
 * отказе определялся языком сервера, а не языком того, кто на него смотрит.
 *
 * Строку, которая под формат не подходит, показываем как есть: так приезжают
 * сообщения библиотек и старых версий api, и потерять их хуже, чем показать
 * не на том языке.
 */

/** Вложенный ключ внутри подстановки: `$t(ssh.authMethod.password)`. */
const NESTED = /\$t\(([^(),]+?)(?:,\s*(\{.*?\}))?\)/g;

/**
 * Перевести вложенные ключи внутри значения подстановки.
 *
 * Нужны там, где сервер собирает сообщение из частей, каждая из которых —
 * тоже словарная строка: «Сервер не принимает вход способом „пароль“»,
 * «Сервер требует аутентификацию: без аутентификации, VeNCrypt». Часть
 * приходит списком, поэтому переводятся все вхождения, а не значение
 * целиком.
 */
function resolveNested(value: unknown, t: TFunction): unknown {
  if (typeof value !== 'string' || !value.includes('$t(')) {
    return value;
  }

  return value.replace(NESTED, (whole, key: string, raw: string | undefined) => {
    let options: Record<string, unknown> = {};

    if (raw !== undefined) {
      try {
        options = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        // Подстановка вложенного ключа испорчена — переведём ключ без неё:
        // «тип {{type}}» заметнее на экране, чем пропавшее сообщение.
      }
    }

    const translated = t(key.trim(), { ...options, defaultValue: '' });

    return translated === '' ? whole : translated;
  });
}

function resolveParams(params: MessageParams, t: TFunction): Record<string, unknown> {
  const resolved: Record<string, unknown> = {};

  for (const [name, value] of Object.entries(params)) {
    resolved[name] = resolveNested(value, t);
  }

  return resolved;
}

/**
 * Текст по ключу, если строка им является. Иначе — `null`.
 *
 * Отдельно от `describeError`, потому что ключи приезжают не только
 * в отказах: тем же форматом записаны сообщения проверок Zod и причина
 * последнего неудачного опроса камеры, сохранённая в базе.
 */
export function translateMessage(message: string, t: TFunction): string | null {
  const parsed = parseMessageKey(message);

  if (parsed === null) {
    return null;
  }

  const text = t(parsed.key, { ...resolveParams(parsed.params, t), defaultValue: '' });

  // Ключа в словаре нет — сервер новее клиента. Показывать сам ключ нельзя:
  // «errors.cameras.disabled» человеку не говорит ничего.
  return text === '' ? null : text;
}

/**
 * Перевести вложенные ключи в обычной строке.
 *
 * Нужно там, где `$t(...)` приезжает не подстановкой в сообщение, а самим
 * значением: название типа безопасности VNC, вид сессии, способ входа SSH.
 */
export function nestedText(value: string, t: TFunction): string {
  return String(resolveNested(value, t));
}

/** То же, но с возвратом исходной строки, когда перевода нет. */
export function messageText(message: string, t: TFunction): string {
  return translateMessage(message, t) ?? message;
}
